import React, { useCallback, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Input,
  Progress,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { InboxOutlined, SearchOutlined } from '@ant-design/icons';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import api from '@/services/api.service';
import {
  getItemTags,
  runRecognition,
  submitRecognitionFeedback,
  updateItemTags,
  type RecognitionCandidate,
  type RecognitionResult,
} from '@/services/recognition.service';

const { Title, Text } = Typography;
const { Dragger } = Upload;

function matchSourceLabel(source: string | null | undefined): string {
  switch (source) {
    case 'qr':
      return 'QR match';
    case 'barcode':
      return 'Barcode match';
    case 'vision':
      return 'Vision match';
    case 'tag':
      return 'Tag match';
    case 'manual_correction':
      return 'Manual correction';
    default:
      return 'Unknown';
  }
}

export const RecognitionLabPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RecognitionResult | null>(null);
  const [correctItemId, setCorrectItemId] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [itemOptions, setItemOptions] = useState<{ value: string; label: string }[]>([]);
  const [tagEditorItemId, setTagEditorItemId] = useState('');
  const [tags, setTags] = useState('');
  const [aliases, setAliases] = useState('');

  const onUpload = useCallback(async (file: File) => {
    setLoading(true);
    setResult(null);
    try {
      const data = await runRecognition(file, 'admin_lab');
      setResult(data);
      if (data.selectedItem) setCorrectItemId(data.selectedItem.id);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || 'Recognition failed');
    } finally {
      setLoading(false);
    }
    return false;
  }, []);

  const searchItems = async () => {
    if (!itemSearch.trim()) return;
    try {
      const res = await api.get('/inventory/items', { params: { search: itemSearch.trim(), item_type: 'Stock' } });
      const body = (res as { data?: unknown })?.data ?? res;
      const list = Array.isArray((body as { data?: unknown[] })?.data)
        ? (body as { data: { id: string; item_code: string; item_name: string }[] }).data
        : Array.isArray(body)
          ? (body as { id: string; item_code: string; item_name: string }[])
          : [];
      setItemOptions(
        list.map((i) => ({ value: i.id, label: `${i.item_code} — ${i.item_name}` }))
      );
    } catch {
      message.error('Item search failed');
    }
  };

  const markCorrect = async () => {
    if (!result?.attemptId || !correctItemId) {
      message.warning('Select the correct product');
      return;
    }
    try {
      const fb = await submitRecognitionFeedback({
        attemptId: result.attemptId,
        correctedItemId: correctItemId,
        source: 'admin_lab',
      });
      message.success(`Correction saved (${fb.appliedTagUpdates.length} tags added)`);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || 'Feedback failed');
    }
  };

  const loadTags = async () => {
    if (!tagEditorItemId) return;
    try {
      const data = await getItemTags(tagEditorItemId);
      setTags(data.tags.map((t) => t.tag).join(', '));
      setAliases(data.aliases.map((a) => a.aliasText).join(', '));
    } catch {
      message.error('Failed to load tags');
    }
  };

  const saveTags = async () => {
    if (!tagEditorItemId) return;
    try {
      await updateItemTags(tagEditorItemId, {
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        aliases: aliases.split(',').map((a) => a.trim()).filter(Boolean),
      });
      message.success('Vision tags updated');
    } catch {
      message.error('Failed to save tags');
    }
  };

  const candidateColumns: ColumnsType<RecognitionCandidate> = [
    { title: 'Code', dataIndex: 'itemCode', width: 120 },
    { title: 'Name', dataIndex: 'itemName', ellipsis: true },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      width: 160,
      render: (v: number) => <Progress percent={Math.round(v * 100)} size="small" />,
    },
    {
      title: 'Signals',
      dataIndex: 'signals',
      ellipsis: true,
      render: (signals: { name: string; score: number }[]) =>
        (signals || []).map((s) => `${s.name}:${s.score.toFixed(2)}`).join(', '),
    },
  ];

  return (
    <div>
      <SmartBreadcrumbs />
      <Title level={3}>Recognition Lab</Title>
      <Text type="secondary">Upload a test image, review candidates, and improve vision tags.</Text>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={10}>
          <Card title="Test image">
            <Dragger
              accept="image/*"
              showUploadList={false}
              beforeUpload={(file) => {
                void onUpload(file);
                return false;
              }}
              disabled={loading}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Drop product photo or click to upload</p>
            </Dragger>
            {result?.coaching && (
              <Alert style={{ marginTop: 12 }} type="info" message={`Coaching: ${result.coaching.replace(/_/g, ' ')}`} />
            )}
            {result?.failureReason && (
              <Alert style={{ marginTop: 12 }} type="warning" message={result.failureReason} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card title="Recognition result" loading={loading}>
            {result ? (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Space wrap>
                  <Tag color="blue">{matchSourceLabel(result.matchSource)}</Tag>
                  <Text>Confidence: {(result.confidence * 100).toFixed(1)}%</Text>
                  <Text type="secondary">Attempt: {result.attemptId.slice(0, 8)}…</Text>
                </Space>
                {result.selectedItem && (
                  <Alert
                    type="success"
                    message={`Selected: ${result.selectedItem.itemCode} — ${result.selectedItem.itemName}`}
                  />
                )}
                <Table
                  size="small"
                  rowKey="itemId"
                  columns={candidateColumns}
                  dataSource={result.candidates}
                  pagination={false}
                />
                <Space.Compact style={{ width: '100%' }}>
                  <Select
                    style={{ flex: 1 }}
                    showSearch
                    placeholder="Mark correct product"
                    options={itemOptions}
                    value={correctItemId || undefined}
                    onChange={setCorrectItemId}
                    filterOption={false}
                  />
                  <Button icon={<SearchOutlined />} onClick={() => void searchItems()}>
                    Search
                  </Button>
                </Space.Compact>
                <Input
                  placeholder="Search items by code or name"
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  onPressEnter={() => void searchItems()}
                />
                <Button type="primary" onClick={() => void markCorrect()} disabled={!correctItemId}>
                  Mark correct product
                </Button>
              </Space>
            ) : (
              <Text type="secondary">Upload an image to run recognition.</Text>
            )}
          </Card>
        </Col>
      </Row>

      <Card title="Vision tags editor" style={{ marginTop: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            showSearch
            placeholder="Select item to edit tags"
            style={{ width: '100%' }}
            options={itemOptions}
            value={tagEditorItemId || undefined}
            onChange={(v) => {
              setTagEditorItemId(v);
            }}
          />
          <Button onClick={() => void loadTags()} disabled={!tagEditorItemId}>
            Load tags
          </Button>
          <Input.TextArea rows={2} placeholder="Vision tags (comma-separated)" value={tags} onChange={(e) => setTags(e.target.value)} />
          <Input.TextArea rows={2} placeholder="Aliases (comma-separated)" value={aliases} onChange={(e) => setAliases(e.target.value)} />
          <Button type="primary" onClick={() => void saveTags()} disabled={!tagEditorItemId}>
            Save tags
          </Button>
        </Space>
      </Card>
    </div>
  );
};
